/**
 * Authentication context object and `useAuth` hook.
 *
 * Kept in a `.ts` file (no JSX, no Provider) so Vite Fast Refresh can treat
 * `AuthProvider.tsx` as a component-only boundary. Mixing the hook and
 * Provider in one `.tsx` file recreates this Context object on HMR while the
 * old Provider stays mounted — blank page: "useAuth must be used within an
 * AuthProvider". See vite-plugin-react "Consistent components exports".
 */

import { createContext, useContext } from 'react';
import type { B2CConfig } from '../config/authConfig';
import type { AuthFlow } from '../constants/auth';

/**
 * User information structure
 *
 * Contains both Entra-synced and database-only user attributes.
 *
 * Entra-synced: email, name, givenName, surname, country
 * Database-only: replyToEmail, profilePictureUrl
 * Token-based: roles (extracted from JWT 'roles' claim)
 */
export interface UserInfo {
  id: string;

  // Entra-synced fields
  email: string;
  name?: string | null;
  givenName?: string | null;
  surname?: string | null;
  country?: string | null;

  // Database-only fields
  replyToEmail?: string | null;
  /** Read-only echo of AppSettings:IncludeReplyToOnDeliveryEmails (recipient delivery Reply-To). */
  includeReplyToOnDeliveryEmails?: boolean;
  profilePictureUrl?: string | null;
  /**
   * User's phone number. Used as default sender phone for flower orders.
   * Optional in My Account; required at flower-order placement and when
   * enabling Flowers AutoSend (the AutoSendUserInfoModal collects it then).
   */
  phone?: string | null;

  // Home address fields (sender/CUSTOMER snapshot source for Florist One)
  // All optional in My Account; required at flower-order placement and when
  // enabling Flowers AutoSend.
  /** Street address line 1 of the user's home address. */
  addressLine1?: string | null;
  /** Street address line 2 — apartment, suite, etc. */
  addressLine2?: string | null;
  /** City of the user's home address. */
  addressCity?: string | null;
  /** State or province of the user's home address. */
  addressState?: string | null;
  /** Postal / ZIP code of the user's home address. */
  addressPostalCode?: string | null;
  /**
   * ISO 3166-1 alpha-2 country code (e.g. "US", "CA").
   * Distinct from the Entra-synced `country` profile field above.
   */
  addressCountry?: string | null;

  // Terms acceptance status
  termsAccepted: boolean;
  /** Non-null when the user accepted a previous version and must re-accept */
  previousTermsVersion?: string | null;

  // Authorization roles (from JWT token)
  roles?: string[];
}

/**
 * Authentication context value type
 */
export interface AuthContextType {
  user: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  termsAccepted: boolean;
  useAzureB2C: boolean;
  b2cConfig: B2CConfig | null;

  // Role-based access control
  /** User's assigned roles */
  roles: string[];
  /** True if user has any admin role */
  isAdmin: boolean;
  /** True if user has SuperAdmin role */
  isSuperAdmin: boolean;
  /** True if user has ContentManager role (or SuperAdmin) */
  canManageContent: boolean;
  /** True if user has SupportAgent role (or SuperAdmin) */
  canAccessSupport: boolean;
  /** Check if user has a specific role */
  hasRole: (role: string) => boolean;

  // Authentication methods
  /**
   * Initiates login redirect to External ID.
   * @param email Ignored for External ID mode (kept for interface compatibility)
   * @param password Ignored for External ID mode (kept for interface compatibility)
   * @param flow Optional auth flow: 'signin' (default) or 'signup'
   */
  login: (email: string, password: string, flow?: AuthFlow) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

/**
 * React context object. Exported for `AuthProvider` only — consumers use `useAuth`.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access authentication context.
 * Must be used within an AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
